/*!
 * Session Timeout Service
 * Location: alfresco-content-app/app/src/app/services/session-timeout.service.ts
 * 
 * Monitors user session and handles timeout/expiration
 */

import { Injectable, OnDestroy, NgZone } from '@angular/core';
import { AuthenticationService, RedirectAuthService } from '@alfresco/adf-core';
import { Subject,  interval, fromEvent, merge } from 'rxjs';
import { takeUntil, throttleTime } from 'rxjs/operators';

export interface SessionConfig {
  // Session timeout in minutes (default: 30)
  timeoutMinutes: number;
  // Warning before timeout in minutes (default: 5)
  warningMinutes: number;
  // Check interval in seconds (default: 30)
  checkIntervalSeconds: number;
  // Extend session on user activity (default: true)
  extendOnActivity: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class SessionTimeoutService implements OnDestroy {
  
  private destroy$ = new Subject<void>();
  private config: SessionConfig = {
    timeoutMinutes: 30,
    warningMinutes: 5,
    checkIntervalSeconds: 30,
    extendOnActivity: true
  };
  
  private lastActivityTime: number = Date.now();
  private warningShown = false;
  private warningCallback: (() => void) | null = null;
  private logoutCallback: (() => void) | null = null;
  private sessionExtendedCallback: (() => void) | null = null;
  
  // Observable for components to subscribe to
  public onWarning$ = new Subject<number>(); // emits remaining seconds
  public onTimeout$ = new Subject<void>();
  public onSessionExtended$ = new Subject<void>();
  
  constructor(
    private authService: AuthenticationService,
    private redirectAuthService: RedirectAuthService,
    private ngZone: NgZone
  ) {}
  
  /**
   * Initialize session monitoring
   */
  initialize(config?: Partial<SessionConfig>): void {
    if (config) {
      this.config = { ...this.config, ...config };
    }
    
    console.log('[SessionTimeout] Initializing with config:', this.config);
    
    this.lastActivityTime = Date.now();
    this.warningShown = false;
    
    // Monitor user activity
    if (this.config.extendOnActivity) {
      this.setupActivityMonitoring();
    }
    
    // Start session check interval
    this.startSessionCheck();
  }
  
  /**
   * Setup monitoring of user activity (mouse, keyboard, touch)
   */
  private setupActivityMonitoring(): void {
    // Run outside Angular zone to avoid triggering change detection
    this.ngZone.runOutsideAngular(() => {
      const activityEvents = merge(
        fromEvent(document, 'mousemove'),
        fromEvent(document, 'mousedown'),
        fromEvent(document, 'keydown'),
        fromEvent(document, 'touchstart'),
        fromEvent(document, 'scroll')
      ).pipe(
        throttleTime(5000), // Only register activity every 5 seconds max
        takeUntil(this.destroy$)
      );
      
      activityEvents.subscribe(() => {
        this.onUserActivity();
      });
    });
  }
  
  /**
   * Called when user activity is detected
   */
  private onUserActivity(): void {
    this.lastActivityTime = Date.now();
    
    // If warning was shown and user is active, extend session
    if (this.warningShown) {
      this.extendSession();
    }
  }
  
  /**
   * Start the session check interval
   */
  private startSessionCheck(): void {
    this.ngZone.runOutsideAngular(() => {
      interval(this.config.checkIntervalSeconds * 1000)
        .pipe(takeUntil(this.destroy$))
        .subscribe(() => {
          this.ngZone.run(() => {
            this.checkSession();
          });
        });
    });
  }
  
  /**
   * Check session status
   */
  private checkSession(): void {
    // Don't check if not logged in
    if (!this.authService.isLoggedIn()) {
      return;
    }
    
    const now = Date.now();
    const inactiveTime = now - this.lastActivityTime;
    const timeoutMs = this.config.timeoutMinutes * 60 * 1000;
    const warningMs = (this.config.timeoutMinutes - this.config.warningMinutes) * 60 * 1000;
    
    // Check if session has timed out
    if (inactiveTime >= timeoutMs) {
      console.log('[SessionTimeout] Session timed out');
      this.handleTimeout();
      return;
    }
    
    // Check if we should show warning
    if (inactiveTime >= warningMs && !this.warningShown) {
      const remainingSeconds = Math.floor((timeoutMs - inactiveTime) / 1000);
      console.log('[SessionTimeout] Showing warning, remaining seconds:', remainingSeconds);
      this.showWarning(remainingSeconds);
    }
    
    // Update warning countdown if already shown
    if (this.warningShown) {
      const remainingSeconds = Math.floor((timeoutMs - inactiveTime) / 1000);
      this.onWarning$.next(remainingSeconds);
    }
  }
  
  /**
   * Show timeout warning
   */
  private showWarning(remainingSeconds: number): void {
    this.warningShown = true;
    this.onWarning$.next(remainingSeconds);
    
    if (this.warningCallback) {
      this.warningCallback();
    }
  }
  
  /**
   * Handle session timeout
   */
  private handleTimeout(): void {
    this.warningShown = false;
    this.onTimeout$.next();
    
    if (this.logoutCallback) {
      this.logoutCallback();
    }
    
    // Perform logout
    this.logout('Session expired due to inactivity');
  }
  
  /**
   * Extend the session (reset timer)
   */
  extendSession(): void {
    console.log('[SessionTimeout] Session extended');
    this.lastActivityTime = Date.now();
    this.warningShown = false;
    this.onSessionExtended$.next();
    
    if (this.sessionExtendedCallback) {
      this.sessionExtendedCallback();
    }
  }
  
  /**
   * Logout and redirect to login page
   */
  logout(reason?: string): void {
    console.log('[SessionTimeout] Logging out:', reason);
    this.stop();
    // Keycloak SSO logout - clears SSO session and redirects to /login cleanly
    this.redirectAuthService.logout();
  }
  
  /**
   * Set callback functions
   */
  setCallbacks(callbacks: {
    onWarning?: () => void;
    onTimeout?: () => void;
    onSessionExtended?: () => void;
  }): void {
    this.warningCallback = callbacks.onWarning || null;
    this.logoutCallback = callbacks.onTimeout || null;
    this.sessionExtendedCallback = callbacks.onSessionExtended || null;
  }
  
  /**
   * Get remaining session time in seconds
   */
  getRemainingTime(): number {
    const now = Date.now();
    const inactiveTime = now - this.lastActivityTime;
    const timeoutMs = this.config.timeoutMinutes * 60 * 1000;
    const remainingMs = timeoutMs - inactiveTime;
    return Math.max(0, Math.floor(remainingMs / 1000));
  }
  
  /**
   * Check if warning is currently shown
   */
  isWarningActive(): boolean {
    return this.warningShown;
  }
  
  /**
   * Stop monitoring (call on logout)
   */
  stop(): void {
    this.destroy$.next();
  }
  
  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}